import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { AI_ROLES, MAX_PHOTOS, uploadForIdentification } from '../utils/aiIdentify';
import AiIdentifyPanel from '../components/AiIdentifyPanel';
import GuidedCapture from '../components/GuidedCapture';

/**
 * Identify any photograph, without first creating a record.
 *
 * Until this page the AI panel was reachable only from Field Mode, after a
 * specimen had been photographed into a draft. Here a person picks photos from
 * the device (or takes one), they are uploaded to storage, and the full
 * ensemble runs on them straight away through POST /ai/identify.
 *
 * Several photographs of ONE specimen can be sent together -- cap, gills,
 * stipe -- and the service fuses them (additional_s3_keys). Photographs of
 * different specimens belong in separate identifications.
 *
 * Two ways to supply them. GUIDED (the default): components/GuidedCapture.js
 * walks through cap, gills and habitat with the photography rules and a
 * hand/foreign-object check, and each photograph is sent with its role
 * (view_roles), which fixes its weight in the fused vector. FREE: any photos,
 * pooled with equal weight, as before.
 *
 * What is deliberately unchanged, because it is the safety design and not a
 * restriction to be unlocked:
 *   - the toxicity banner and per-candidate warnings, and no edibility, ever;
 *   - the roles that may request identifications (routes/ai.py REQUEST_ROLES):
 *     the self-registered `public` role is excluded, as everywhere, and
 *     `read_only` accounts write nothing, identification rows included;
 *   - the result is a suggestion, and `sawAiFirst` is set, so this feedback is
 *     never mistaken for an expert's independent identification in training.
 * The daily quota is a server setting: the desktop runs with none, because
 * inference there is local and costs nothing (desktop/bootstrap.py).
 */
export { AI_ROLES, MAX_PHOTOS, uploadForIdentification };

export default function Identify() {
  const { t } = useTranslation('ai');
  const { user } = useAuth();
  const allowed = (user?.roles || []).some(r => AI_ROLES.includes(r));

  const [items, setItems] = useState([]);          // [{ file, preview }]
  const [phase, setPhase] = useState('choose');    // choose | uploading | ready | failed
  const [keys, setKeys] = useState([]);
  const [notice, setNotice] = useState('');
  const [mode, setMode] = useState('guided');      // guided | free
  const [roles, setRoles] = useState(null);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => () => {
    itemsRef.current.forEach(i => i.preview && URL.revokeObjectURL(i.preview));
  }, []);

  // Ask the cloud's inference Lambda to load its models while the photographs
  // are being taken (POST /ai/warm, routes/ai.py): a cold one takes longer to
  // load than an identification waits. Fire and forget: nothing waits on it.
  // Its answer also says when identification runs only in production (dev and
  // staging: `reason: prod_only`), which the page says before any photograph.
  const [prodOnly, setProdOnly] = useState(false);
  useEffect(() => {
    if (!allowed) return undefined;
    let live = true;
    api.post('/ai/warm')
      .then((res) => { if (live && res?.data?.reason === 'prod_only') setProdOnly(true); })
      .catch(() => {});
    return () => { live = false; };
  }, [allowed]);

  const add = (fileList) => {
    const chosen = Array.from(fileList || []);
    const images = chosen.filter(f => (f.type || '').startsWith('image/'));
    const rejected = chosen.filter(f => !images.includes(f)).map(f => f.name);
    setNotice(rejected.length ? t('identifyPage.notImage', { names: rejected.join(', ') }) : '');
    setItems(prev => {
      const next = [...prev, ...images.map(file => ({
        file,
        preview: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
      }))];
      if (next.length > MAX_PHOTOS) {
        next.slice(MAX_PHOTOS).forEach(i => i.preview && URL.revokeObjectURL(i.preview));
        setNotice(t('identifyPage.maxPhotos', { max: MAX_PHOTOS }));
      }
      return next.slice(0, MAX_PHOTOS);
    });
  };

  const remove = (index) => setItems(prev => {
    const gone = prev[index];
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return prev.filter((_, i) => i !== index);
  });

  const upload = async (files, withRoles) => {
    setPhase('uploading');
    setNotice('');
    try {
      const uploaded = await uploadForIdentification(files);
      setKeys(uploaded);
      setRoles(withRoles);
      setPhase('ready');
    } catch {
      setPhase('failed');
    }
  };

  const identify = () => upload(items.map(i => i.file), null);
  const identifyGuided = ({ files, roles: taken }) => upload(files, taken);

  const reset = () => {
    items.forEach(i => i.preview && URL.revokeObjectURL(i.preview));
    setItems([]);
    setKeys([]);
    setRoles(null);
    setNotice('');
    setPhase('choose');
  };

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={titleStyle}>{t('identifyPage.title')}</h2>
        <p style={noticeStyle}>{t('identifyPage.notPermitted')}</p>
      </div>
    );
  }

  const busy = phase === 'uploading';
  const showPicker = phase !== 'ready';

  return (
    <div style={pageStyle}>
      <h2 style={titleStyle}>{t('identifyPage.title')}</h2>
      <p style={introStyle}>{t('identifyPage.intro')}</p>
      {prodOnly && <p style={noticeStyle} role="note" data-testid="identify-prod-only">{t('prodOnly')}</p>}

      {showPicker && (
        <div role="group" aria-label={t('identifyPage.mode')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button type="button" aria-pressed={mode === 'guided'} onClick={() => setMode('guided')} disabled={busy}
                  title={t('tip.identify.modeGuided')}
                  style={toggleStyle(mode === 'guided')}>{t('identifyPage.modeGuided')}</button>
          <button type="button" aria-pressed={mode === 'free'} onClick={() => setMode('free')} disabled={busy}
                  title={t('tip.identify.modeFree')}
                  style={toggleStyle(mode === 'free')}>{t('identifyPage.modeFree')}</button>
        </div>
      )}

      {showPicker && mode === 'guided' && (
        <>
          <GuidedCapture onComplete={identifyGuided} disabled={busy} />
          {busy && <p style={hintStyle}>{t('identifyPage.uploading')}</p>}
        </>
      )}

      {showPicker && mode === 'free' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <button type="button" onClick={() => galleryRef.current?.click()} disabled={busy || items.length >= MAX_PHOTOS}
                    title={t('tip.identify.choose', { max: MAX_PHOTOS })}
                    style={btn('var(--c-primary)')}>🗂 {t('identifyPage.choose')}</button>
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={busy || items.length >= MAX_PHOTOS}
                    title={t('tip.identify.camera', { max: MAX_PHOTOS })}
                    style={btn('var(--c-info)')}>📷 {t('identifyPage.camera')}</button>
            <input ref={galleryRef} type="file" accept="image/*" multiple data-testid="identify-gallery-input" title={t('tip.identify.choose', { max: MAX_PHOTOS })}
                   style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" data-testid="identify-camera-input" title={t('tip.identify.camera', { max: MAX_PHOTOS })}
                   style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
          </div>
          <p style={hintStyle}>{t('identifyPage.maxPhotos', { max: MAX_PHOTOS })}</p>
        </>
      )}

      {notice && <p style={noticeStyle}>{notice}</p>}

      {mode === 'free' && items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '10px 0' }}>
          {items.map((item, i) => (
            <figure key={`${item.file.name}-${i}`} data-testid="identify-photo" style={{ margin: 0, position: 'relative', width: 120 }}>
              {item.preview
                ? <img src={item.preview} alt={item.file.name} style={thumbStyle} />
                : <div style={{ ...thumbStyle, background: 'var(--c-bg-muted)' }} />}
              <figcaption style={captionStyle}>{item.file.name}</figcaption>
              {showPicker && !busy && (
                <button type="button" onClick={() => remove(i)} aria-label={`${t('identifyPage.remove')} ${item.file.name}`}
                        title={t('tip.identify.remove')}
                        style={removeStyle}>×</button>
              )}
            </figure>
          ))}
        </div>
      )}

      {showPicker && mode === 'free' && items.length > 0 && (
        <button type="button" onClick={identify} disabled={busy} title={t('tip.identify.identify')} style={{ ...btn('var(--c-primary)'), fontSize: 15, padding: '8px 18px' }}>
          {busy ? t('identifyPage.uploading') : t('identifyPage.identify', { count: items.length })}
        </button>
      )}

      {phase === 'failed' && <p style={errorStyle}>{t('identifyPage.uploadFailed')}</p>}

      {phase === 'ready' && keys.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <AiIdentifyPanel key={keys.join('|')} s3Key={keys[0]} additionalS3Keys={keys.slice(1)}
                           viewRoles={roles || undefined} sawAiFirst autoRun />
          <button type="button" onClick={reset} title={t('tip.identify.another')} style={{ ...btn('var(--c-text-muted)'), marginTop: 12 }}>
            {t('identifyPage.another')}
          </button>
        </div>
      )}
    </div>
  );
}

const pageStyle = { maxWidth: 820, margin: '0 auto', padding: '8px 4px' };
const titleStyle = { margin: '0 0 6px', color: 'var(--c-primary-fg)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: '0 0 14px', lineHeight: 1.5 };
const hintStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' };
const noticeStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px' };
const thumbStyle = { width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)', display: 'block' };
const captionStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const removeStyle = {
  position: 'absolute', top: 2, insetInlineEnd: 2, background: 'rgba(0,0,0,0.55)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: 14, lineHeight: '20px', padding: 0,
};
const toggleStyle = (active) => ({
  background: active ? 'var(--c-primary)' : 'var(--c-surface)', color: active ? 'var(--c-on-accent)' : 'var(--c-primary-fg)', border: '1px solid var(--c-primary)',
  borderRadius: 16, padding: '4px 12px', cursor: 'pointer', fontSize: 13,
});
const btn = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 14 });
